import BackrsFilledDropShadow from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { PanelNuggetActionParams } from '@backrs_ui/common-ui/src/models/nugget';

import { Box, SxProps, Theme } from '@mui/material';

import { BadgeIcon, BadgeIconProps } from './BadgeIcon';
import { BASE_NUGGET_IMAGE_MAX_HEIGHT } from './BaseNugget';
import { HoverButton } from './HoverButton';
import { NuggetUXVariant } from './NuggetUITypes';
import { SponsorChip, SponsorChipProps } from './SponsorChip';

const ToolImage = ({ imageUrl }: { imageUrl: string | undefined }) => {
  return (
    <Box component="img" src={imageUrl || BackrsFilledDropShadow} alt="Tool" sx={toolImageSx} />
  );
};

const badgeIconConfig: Partial<Record<NuggetUXVariant['type'], BadgeIconProps>> = {
  opportunity: {
    backgroundColor: 'Warning/400',
    icon: 'Briefcase01',
  },
  'video-internal': {
    backgroundColor: undefined,
    icon: 'VideoRecorder',
  },
  'video-external': {
    backgroundColor: undefined,
    icon: 'VideoRecorder',
  },
  article: {
    backgroundColor: undefined,
    icon: 'File06',
  },
  institution: {
    backgroundColor: undefined,
    icon: 'Bank',
  },
  employer: {
    backgroundColor: undefined,
    icon: 'Building01',
  },
  audio: {
    backgroundColor: undefined,
    icon: 'VolumeMin',
  },
  document: {
    backgroundColor: undefined,
    icon: 'File02',
  },
  resource: {
    backgroundColor: undefined,
    icon: 'BookOpen01',
  },
  'text-response': {
    backgroundColor: undefined,
    icon: 'File02',
  },
  book: {
    backgroundColor: undefined,
    icon: 'BookOpen01',
  },
  scholarships: {
    backgroundColor: 'Success/500',
    icon: 'CurrencyDollarCircle',
  },
  internship: {
    backgroundColor: 'Warning/400',
    icon: 'Briefcase01',
  },
  'generated-content': {
    backgroundColor: undefined,
    icon: 'File02',
  },
  'career-path': {
    backgroundColor: 'Brand/500',
    icon: 'Palette',
  },
  competency: {
    backgroundColor: 'Brand/200',
    icon: 'BookOpen01',
  },
  'career-track': {
    backgroundColor: 'Brand/500',
    iconColor: 'Colors/Base/White',
    icon: 'Route',
  },
  major: {
    backgroundColor: 'Error/400',
    icon: 'Stand',
  },
  industry: {
    backgroundColor: 'Brand/700',
    icon: 'ShieldDollar',
    iconColor: 'Colors/Base/White',
  },
};

const toolImageSx: SxProps<Theme> = {
  width: ICON_SIZES['96px'],
  height: ICON_SIZES['96px'],
  objectFit: 'cover',
};

type NoPreviewHeaderProps = Readonly<{
  variant: NuggetUXVariant;
  actionParams?: PanelNuggetActionParams;
  isHovered: boolean;
  sponsorChipProps?: SponsorChipProps[];
  customBadgeIcon?: BadgeIconProps;
}>;

export const NoPreviewHeader = ({
  variant,
  actionParams,
  isHovered,
  sponsorChipProps,
  customBadgeIcon,
}: NoPreviewHeaderProps) => {
  const badgeConfig = badgeIconConfig[variant.type];

  // special case for tool variant to show the tool image rather than the badge icon
  const isToolVariant = variant.type === 'tool';
  const toolImageUrl = isToolVariant ? variant.toolInfo?.media?.media_url : undefined;

  return (
    <Box sx={containerSx}>
      <Box sx={badgeContainerSx}>
        {isToolVariant ? (
          <ToolImage imageUrl={toolImageUrl} />
        ) : badgeConfig || customBadgeIcon ? (
          <BadgeIcon {...(customBadgeIcon || badgeConfig)} />
        ) : null}
        {actionParams && (
          <HoverButton
            hasBookmarked={actionParams?.hasBookmarked}
            handleActionBookmark={actionParams?.handleActionBookmark}
            handleActionAddTask={actionParams?.handleActionAddTask}
            handleActionShare={actionParams?.handleActionShare}
            isVisible={isHovered}
            isAbsolutelyPositioned={true}
            position="flush"
          />
        )}
      </Box>
      {sponsorChipProps && (
        <Box sx={sponsorChipOverlaySx}>
          {sponsorChipProps.map((sponsor) => (
            <SponsorChip
              key={sponsor.label}
              label={sponsor.label}
              iconImage={sponsor.iconImage}
              defaultIcon={sponsor.defaultIcon}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const badgeContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  width: '100%',
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '16/9',
  maxHeight: BASE_NUGGET_IMAGE_MAX_HEIGHT,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexDirection: 'column',
  marginBottom: backrs.spacing['spacing-sm'],
});

const sponsorChipOverlaySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  columnGap: backrs.spacing['spacing-md'],
  flexDirection: 'row',
  flexWrap: 'wrap',
  rowGap: backrs.spacing['spacing-xs'],
  zIndex: 1,
});
